'use client'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { downloadCsvExport } from '@/lib/csv-export'
import { ChevronDown, Download } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import exportResultsToCsv from '../actions/export-results-to-csv'

export default function StandingsCsvExport({
  championship,
  eventId,
  compact = false,
}) {
  const [isExporting, setIsExporting] = useState(false)

  async function handleExport(includeRoundBreakdown = false) {
    setIsExporting(true)
    toast.loading('Generating CSV export...', { id: 'results-csv-export' })

    try {
      const csvData = await exportResultsToCsv({
        communityId: championship.communityId,
        championshipId: championship.id,
        eventId: eventId || null,
        includeRoundBreakdown,
      })

      if (!csvData.rows.length) {
        toast.error('No published results available to export', {
          id: 'results-csv-export',
        })
        return
      }

      downloadCsvExport(
        csvData,
        eventId ? 'event-results.csv' : 'standings.csv',
      )
      toast.success(`Exported ${csvData.rows.length} drivers to CSV`, {
        id: 'results-csv-export',
      })
    } catch (error) {
      console.error('Failed to export results CSV:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to export results',
        { id: 'results-csv-export' },
      )
    } finally {
      setIsExporting(false)
    }
  }

  const exportLabel = eventId
    ? 'Export event results CSV'
    : 'Export championship standings CSV'

  if (eventId) {
    if (compact) {
      return (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="square"
                className="h-[52px] w-[52px] shrink-0"
                aria-label={exportLabel}
                disabled={isExporting}
                onClick={() => handleExport()}
              >
                <Download className="size-5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{exportLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return (
      <Button
        type="button"
        variant="outline"
        className="gap-2"
        disabled={isExporting}
        onClick={() => handleExport()}
      >
        <Download className="size-4" aria-hidden="true" />
        {isExporting ? 'Exporting...' : 'Export Results CSV'}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      {compact ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="square"
                  className="h-[52px] w-[52px] shrink-0"
                  aria-label={exportLabel}
                  disabled={isExporting}
                >
                  <Download className="size-5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{exportLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            aria-label={exportLabel}
            disabled={isExporting}
          >
            <Download className="size-4" aria-hidden="true" />
            {isExporting ? 'Exporting...' : 'Export Standings CSV'}
            <ChevronDown className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => handleExport(false)}>
          Standings summary
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExport(true)}>
          Include round breakdown
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
